import React from 'react';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useUserPermissions } from '../hooks/useUserPermissions';
import { Routes } from '../types/enums/Routes.enum';
import { AccessDenied } from './AccessDenied';

/**
 * Props para el componente ProtectedAdminRoute
 */
interface ProtectedAdminRouteProps {
  children: ReactNode;
  fallbackComponent?: ReactNode;
  redirectTo?: string;
  showAccessDenied?: boolean;
}

/**
 * Componente que protege rutas que requieren rol de Administrador
 * Usa el helper obtenerTipoUsuario para verificar el rol del usuario
 * 
 * @example
 * ```tsx
 * <ProtectedAdminRoute>
 *   <EquipoPage />
 * </ProtectedAdminRoute>
 * ```
 */
export const ProtectedAdminRoute: React.FC<ProtectedAdminRouteProps> = ({
  children,
  fallbackComponent,
  redirectTo = Routes.DEFAULT,
  showAccessDenied = true,
}) => {
  const { isAdministrador } = useUserPermissions();
  const location = useLocation();

  /* Si no es administrador */
  if (!isAdministrador) {
    /* Si hay un componente de fallback personalizado, usarlo */
    if (fallbackComponent) {
      return <>{fallbackComponent}</>;
    }

    /* Si se debe mostrar el componente de acceso denegado */
    if (showAccessDenied) {
      return (
        <AccessDenied
          variant="detailed"
          title="Acceso Restringido"
          message="Esta funcionalidad está disponible únicamente para usuarios con rol de Administrador."
          showContactInfo
        />
      );
    }

    /* Redirigir a la ruta especificada */
    return (
      <Navigate
        to={redirectTo}
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  /* Si es administrador, renderizar el contenido */
  return <>{children}</>;
};
